import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Bell } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  notificationsPreviewOptions,
  unreadCountOptions,
} from "@/lib/queries";
import {
  invalidateNotifications,
  patchUnread,
  restoreUnread,
} from "@/lib/optimisticNotifications";
import {
  describeNotification,
  markAllNotificationsRead,
  markNotificationRead,
  NOTIFICATION_COLOR,
  notificationsPreviewView,
  timeAgo,
  type NotificationItem,
} from "@/lib/notifications";

const PREVIEW_LIMIT = 8;

export function NotificationsBell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  // Polls every 60s via the query's refetchInterval.
  const { data: unread = 0 } = useQuery(unreadCountOptions);

  // The preview list only fetches while the dropdown is open, but we warm it on
  // hover/focus of the bell (see `prefetchPreview`) so the data is usually
  // already cached by the time the menu opens — no visible loading flash.
  const { data: preview, isPending, isError } = useQuery({
    ...notificationsPreviewOptions(PREVIEW_LIMIT),
    enabled: open,
  });
  const items = preview?.notifications ?? [];
  const view = notificationsPreviewView({
    open,
    isPending,
    isError,
    count: items.length,
  });

  // Kick off the preview fetch before the click lands. `prefetchQuery` respects
  // the option's staleTime, so a warm cache is a no-op.
  const prefetchPreview = () =>
    void queryClient.prefetchQuery(notificationsPreviewOptions(PREVIEW_LIMIT));

  const settle = () => invalidateNotifications(queryClient);

  const markAll = useMutation({
    mutationFn: markAllNotificationsRead,
    onMutate: () => ({ prev: patchUnread(queryClient, () => 0) }),
    onError: (_e, _v, ctx) => restoreUnread(queryClient, ctx?.prev),
    onSettled: settle,
  });

  const markRead = useMutation({
    mutationFn: markNotificationRead,
    onMutate: () => ({ prev: patchUnread(queryClient, (n) => n - 1) }),
    onError: (_e, _id, ctx) => restoreUnread(queryClient, ctx?.prev),
    onSettled: settle,
  });

  function handleItemClick(n: NotificationItem) {
    if (!n.readAt) markRead.mutate(n.id);
    setOpen(false);
    if (n.taskId) {
      void navigate({ to: "/", search: { task: n.taskId } });
    } else {
      void navigate({ to: "/notifications" });
    }
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          className="relative"
          aria-label="Notifications"
          onMouseEnter={prefetchPreview}
          onFocus={prefetchPreview}
        >
          <Bell className="mt-px size-4" />
          {unread > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-signoff-red px-1 text-[10px] font-semibold text-white">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="text-sm font-semibold">Notifications</span>
          {unread > 0 && (
            <button
              onClick={() => markAll.mutate()}
              className="cursor-pointer text-xs text-muted-foreground hover:text-foreground"
            >
              Mark all read
            </button>
          )}
        </div>

        <div className="max-h-80 overflow-y-auto">
          {view === "loading" ? (
            <p className="px-3 py-6 text-center text-xs text-muted-foreground">
              Loading…
            </p>
          ) : view === "error" ? (
            <p className="px-3 py-6 text-center text-xs text-destructive">
              Couldn't load notifications.
            </p>
          ) : view === "empty" ? (
            <p className="px-3 py-6 text-center text-xs text-muted-foreground">
              You're all caught up.
            </p>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                onClick={() => handleItemClick(n)}
                className={`flex w-full cursor-pointer gap-2.5 px-3 py-2.5 text-left text-sm transition-colors hover:bg-muted/60 ${n.readAt ? "" : "bg-muted/40"}`}
              >
                <span
                  aria-hidden="true"
                  className="mt-1.5 size-2 shrink-0 rounded-full"
                  style={{ backgroundColor: NOTIFICATION_COLOR[n.type] }}
                />
                <div className="min-w-0 flex-1">
                  <p className="line-clamp-2 leading-snug">
                    {describeNotification(n)}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {timeAgo(n.createdAt)}
                  </p>
                </div>
              </button>
            ))
          )}
        </div>

        <Link
          to="/notifications"
          onClick={() => setOpen(false)}
          className="block w-full cursor-pointer border-t border-border px-3 py-2 text-center text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          See all notifications
        </Link>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default NotificationsBell;
